import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Helpers } from 'src/app/app.helpers';
import { map } from 'rxjs/operators'; // Add this line
import config from '../../auth_config.json';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private http: HttpClient, public helpers: Helpers) { }

  postJobs$(orgId: string, job: any): Observable<any> {
    return this.http.post(`${config.apiUri}/jobs?org_id=${orgId}`, job);
  }

  copyJobs$(orgId: string, job): Observable<any> {
    var cloneJob = this.helpers.deepCopy(job);

    // Prepare the job for posting
    cloneJob.id = null;
    cloneJob.artifacts = null;
    cloneJob.is_archived = null;
    cloneJob.is_deleted = null;
    cloneJob.deleted_at = null;
    cloneJob.title = cloneJob.title + ' (copy)';

    // Get rid of the cloneJob.data items where item.section starts with "calculated_"
    for (let i = 0; i < cloneJob.data.length; i++) {
      if (cloneJob.data[i].section.startsWith('calculated_')) {
        cloneJob.data.splice(i, 1);
        i--;
      }
    }

    return this.http.post(`${config.apiUri}/jobs?org_id=${orgId}`, cloneJob);
  }

  copyAsAnalysisType$(orgId: string, job, analysisType: string): Observable<any> {
    var cloneJob = this.helpers.deepCopy(job);

    cloneJob.id = null;
    cloneJob.artifacts = null;
    cloneJob.type = analysisType + job.type.substring(job.type.indexOf('_'));

    // if analysisType == 'mechanical' then set everything after the last '_' to 'none'
    if (analysisType == 'mechanical') {
      cloneJob.type = cloneJob.type.substring(0, cloneJob.type.lastIndexOf('_') + 1) + 'none';
    }

    // if analysisType == 'thermal' then set everything between the first and last '_' to 'none'
    if (analysisType == 'thermal') {
      cloneJob.type = cloneJob.type.substring(0, cloneJob.type.indexOf('_') + 1) + 'none' + cloneJob.type.substring(cloneJob.type.lastIndexOf('_'));
    }

    // Get rid of the cloneJob.data items where item.section starts with "calculated_"
    for (let i = 0; i < cloneJob.data.length; i++) {
      if (cloneJob.data[i].section.startsWith('calculated_')) {
        cloneJob.data.splice(i, 1);
        i--;
      }
    }

    return this.http.post(`${config.apiUri}/jobs?org_id=${orgId}`, cloneJob);
  }

  putJobsId$(id: string, job: any): Observable<any> {
    return this.http.put(`${config.apiUri}/jobs/${id}`, job);
  }

  getJobs$(orgId: string, fetch_type: number, limit: number): Observable<any> {
    return this.http.get(`${config.apiUri}/jobs?org_id=${orgId}&fetch_type=${fetch_type}&limit=${limit}`);
  }

  getJobsId$(id: string): Observable<any> {
    return this.http.get(`${config.apiUri}/jobs/${id}`);
  }

  getGeneratedName$(): Observable<{ name: string }> {
    return this.http.get<{ name: string }>(`${config.apiUri}/names/generate`);
  }

  putJobsIdQueue$(id: string): Observable<any> {
    return this.http.put(`${config.apiUri}/jobs/${id}/status/10`, {});
  }

  promoteJobArtifact$(id: string, artifactId: string): Observable<any> {
    return this.http.put(`${config.apiUri}/jobs/${id}/artifacts/${artifactId}/promote`, {});
  }

  rollupJobArtifacts$(id: string): Observable<any> {
    return this.http.put(`${config.apiUri}/jobs/${id}/artifacts/rollup`, {});
  }

  deleteJobsId$(id: string, deleteType: number): Observable<any> {
    return this.http.delete(`${config.apiUri}/jobs/${id}?delete_type=${deleteType}`);
  }

  getMaterials$(): Observable<any> {
    return this.http.get(`${config.apiUri}/materials`);
  }

  getMaterialsId$(id: string): Observable<any> {
    return this.http.get(`${config.apiUri}/materials/${id}`);
  }

  deleteMaterialsId$(id: string): Observable<any> {
    console.log(config.apiUri);
    return this.http.delete(`${config.apiUri}/materials/${id}`);
  }

  getUsersId$(id: string): Observable<any> {
    return this.http.get(`${config.apiUri}/users/${id}`);
  }

  getUsersIdOrganisations$(id: string): Observable<any> {
    return this.http.get(`${config.apiUri}/users/${id}/organisations`);
  }

  getClusters$(): Observable<any> {
    return this.http.get(`${config.apiUri}/clusters`);
  }

  deleteClustersId$(id: string): Observable<any> {
    return this.http.delete(`${config.apiUri}/clusters/${id}`);
  }

  postGetWinding$(rotorType: string, statorType: string, winding_params: any): Observable<any> {
    return rotorType === "none" || statorType === "none"
      ? new Observable((observer) => {
        observer.next(null);
        observer.complete();
      })
      : this.http.post(`${config.windingUri}/winding`, winding_params)
        .pipe(
          map(response => ({ ...response, ...winding_params }))
        );
  }

  postGetWindingNetlist$(windingParams: any): Observable<any> {
    return this.http.post(`${config.windingUri}/winding_netlist`, windingParams);
  }

  postGetJobNetlist$(job: any): Observable<any> {
    var winding_param = this.helpers.job2winding(job);
    return this.postGetWindingNetlist$(winding_param);
  }

  postGetRotorPartsGeometry$(rotorType: string, value: any): Observable<any> {
    return rotorType == "none"
      ? new Observable((observer) => {
        observer.next(null);
        observer.complete();
      })
      : this.http.post(`${config.geometriesUri}/rotors/${rotorType}`, value);
  }

  postGetStatorPartsGeometry$(statorType: string, value: any): Observable<any> {
    return statorType == "none"
      ? new Observable((observer) => {
        observer.next(null);
        observer.complete();
      })
      : this.http.post(`${config.geometriesUri}/stators/${statorType}`, value);
  }

  postOrganisations$(value: any): Observable<any> {
    return this.http.post(`${config.apiUri}/organisations`, value);
  }

  putOrganisationsId$(id: string, value: any): Observable<any> {
    return this.http.put(`${config.apiUri}/organisations/${id}`, value);
  }

  putOrganisationsIdJoin$(id: string): Observable<any> {
    return this.http.put(`${config.apiUri}/organisations/${id}/join`, {});
  }

  putOrganisationsIdLeave$(id: string): Observable<any> {
    return this.http.put(`${config.apiUri}/organisations/${id}/leave`, {});
  }

  getOrganisationsId$(id: string): Observable<any> {
    return this.http.get(`${config.apiUri}/organisations/${id}`);
  }

  getOrganisationsIdUsers$(id: string): Observable<any> {
    return this.http.get(`${config.apiUri}/organisations/${id}/users`);
  }

  getOrganisations$(): Observable<any> {
    return this.http.get(`${config.apiUri}/organisations`);
  }

  postRequestCpuTimeMessage$(org: any): Observable<any> {

    var value = {
      "body": "Requesting CPU time"
    };

    return this.http.post(`${config.apiUri}/messages?org_id=${org.id}`, value);
  }

  getMessages$(): Observable<any> {
    return this.http.get(`${config.apiUri}/messages`);
  }

  deleteMessagesId$(id: string): Observable<any> {
    return this.http.delete(`${config.apiUri}/messages/${id}`);
  }

  getLogs$(): Observable<any> {
    return this.http.get(`${config.apiUri}/logs`);
  }

  getLogsId$(id: string): Observable<any> {
    return this.http.get(`${config.apiUri}/logs/${id}`);
  }

  deleteLogssId$(id: string): Observable<any> {
    return this.http.delete(`${config.apiUri}/logs/${id}`);
  }
}